import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  Share2, Building2, Plane, Users, Shield, Check, X, Clock, 
  Plus, Eye, RefreshCw, AlertTriangle, Handshake, Network, History
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

const SHARING_LEVELS = [
  { value: 'full', label: 'Full Access', description: 'Complete access to all safety data' },
  { value: 'summary', label: 'Summary Only', description: 'Aggregated data only, no detailed records' },
  { value: 'incidents', label: 'Incidents Only', description: 'Only incident notifications' },
];

const STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  active: 'bg-green-500/20 text-green-400 border-green-500/30',
  expired: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  revoked: 'bg-red-500/20 text-red-400 border-red-500/30',
  rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
};

export default function DataSharingPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [agreements, setAgreements] = useState([]);
  const [groups, setGroups] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [aircraft, setAircraft] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState(null);

  const [newAgreement, setNewAgreement] = useState({
    sharer_organization_id: '',
    recipient_organization_id: '',
    sharing_level: 'summary',
    aircraft_ids: [],
    effective_date: new Date().toISOString().split('T')[0],
    expiry_date: '',
    share_incidents: true,
    share_risks: true,
    share_component_data: true,
    share_life_limit_alerts: true,
  });

  const [newGroup, setNewGroup] = useState({
    group_name: '',
    group_type: 'holding_company',
    parent_organization_id: '',
    consolidate_safety_metrics: true,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [agrRes, grpRes, tfrRes, orgRes, acRes] = await Promise.all([
        api.get('/data-sharing/agreements'),
        api.get('/data-sharing/groups'),
        api.get('/data-sharing/transfers'),
        api.get('/organizations'),
        api.get('/aircraft'),
      ]);
      setAgreements(agrRes.data || []);
      setGroups(grpRes.data || []);
      setTransfers(tfrRes.data || []);
      setOrganizations(orgRes.data || []);
      setAircraft(acRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data sharing information');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateAgreement = async () => {
    try {
      await api.post('/data-sharing/agreements', newAgreement);
      toast.success('Data sharing agreement created');
      setShowCreateDialog(false);
      setNewAgreement({
        sharer_organization_id: '',
        recipient_organization_id: '',
        sharing_level: 'summary',
        aircraft_ids: [],
        effective_date: new Date().toISOString().split('T')[0],
        expiry_date: '',
        share_incidents: true,
        share_risks: true,
        share_component_data: true,
        share_life_limit_alerts: true,
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create agreement');
    }
  };

  const handleApproveAgreement = async (agreementId) => {
    try {
      await api.post(`/data-sharing/agreements/${agreementId}/approve`);
      toast.success('Agreement approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve agreement');
    }
  };

  const handleRevokeAgreement = async (agreementId) => {
    if (!window.confirm('Are you sure you want to revoke this agreement?')) return;
    try {
      await api.post(`/data-sharing/agreements/${agreementId}/revoke`);
      toast.success('Agreement revoked');
      fetchData();
    } catch (error) {
      toast.error('Failed to revoke agreement');
    }
  };

  const handleCreateGroup = async () => {
    try {
      await api.post('/data-sharing/groups', newGroup);
      toast.success('Airline group created');
      setShowGroupDialog(false);
      setNewGroup({
        group_name: '',
        group_type: 'holding_company',
        parent_organization_id: '',
        consolidate_safety_metrics: true,
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create group');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
            Data Sharing & Collaboration
          </h1>
          <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
            Manage data sharing agreements between organizations, airline groups, and safety history transfers
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className={cn("w-4 h-4", loading && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-sky-500/20">
                <Handshake className="w-5 h-5 text-sky-500" />
              </div>
              <div>
                <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                  {agreements.filter(a => a.status === 'active').length}
                </div>
                <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                  Active Agreements
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-yellow-500/20">
                <Clock className="w-5 h-5 text-yellow-500" />
              </div>
              <div>
                <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                  {agreements.filter(a => a.status === 'pending').length}
                </div>
                <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                  Pending Approval
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/20">
                <Network className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                  {groups.length}
                </div>
                <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                  Airline Groups
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/20">
                <History className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <div className={cn("text-2xl font-bold", isDark ? "text-white" : "text-gray-900")}>
                  {transfers.length}
                </div>
                <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                  History Transfers
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="agreements" className="w-full">
        <TabsList className={cn("grid w-full grid-cols-3", isDark ? "bg-slate-800" : "")}>
          <TabsTrigger value="agreements">Sharing Agreements</TabsTrigger>
          <TabsTrigger value="groups">Airline Groups</TabsTrigger>
          <TabsTrigger value="transfers">History Transfers</TabsTrigger>
        </TabsList>

        {/* Agreements Tab */}
        <TabsContent value="agreements" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className={cn("text-lg font-semibold", isDark ? "text-white" : "")}>
              Data Sharing Agreements
            </h2>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-sky-600 hover:bg-sky-700 gap-2">
                  <Plus className="w-4 h-4" />
                  New Agreement
                </Button>
              </DialogTrigger>
              <DialogContent className={cn("max-w-lg", isDark ? "bg-slate-900 border-slate-800" : "")}>
                <DialogHeader>
                  <DialogTitle className={isDark ? "text-white" : ""}>Create Sharing Agreement</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className={isDark ? "text-slate-300" : ""}>Sharer Organization *</Label>
                    <Select 
                      value={newAgreement.sharer_organization_id} 
                      onValueChange={(v) => setNewAgreement({...newAgreement, sharer_organization_id: v})}
                    >
                      <SelectTrigger className={isDark ? "bg-slate-950 border-slate-800" : ""}>
                        <SelectValue placeholder="Select organization..." />
                      </SelectTrigger>
                      <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                        {organizations.map(org => (
                          <SelectItem key={org.id} value={org.id}>{org.legal_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className={isDark ? "text-slate-300" : ""}>Recipient Organization *</Label>
                    <Select 
                      value={newAgreement.recipient_organization_id} 
                      onValueChange={(v) => setNewAgreement({...newAgreement, recipient_organization_id: v})}
                    >
                      <SelectTrigger className={isDark ? "bg-slate-950 border-slate-800" : ""}>
                        <SelectValue placeholder="Select organization..." />
                      </SelectTrigger>
                      <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                        {organizations.filter(o => o.id !== newAgreement.sharer_organization_id).map(org => (
                          <SelectItem key={org.id} value={org.id}>{org.legal_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className={isDark ? "text-slate-300" : ""}>Sharing Level</Label>
                    <Select 
                      value={newAgreement.sharing_level} 
                      onValueChange={(v) => setNewAgreement({...newAgreement, sharing_level: v})}
                    >
                      <SelectTrigger className={isDark ? "bg-slate-950 border-slate-800" : ""}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                        {SHARING_LEVELS.map(level => (
                          <SelectItem key={level.value} value={level.value}>
                            <div>
                              <div>{level.label}</div>
                              <div className="text-xs text-slate-400">{level.description}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className={isDark ? "text-slate-300" : ""}>Effective Date *</Label>
                      <Input 
                        type="date" 
                        value={newAgreement.effective_date}
                        onChange={(e) => setNewAgreement({...newAgreement, effective_date: e.target.value})}
                        className={isDark ? "bg-slate-950 border-slate-800" : ""}
                      />
                    </div>
                    <div>
                      <Label className={isDark ? "text-slate-300" : ""}>Expiry Date</Label>
                      <Input 
                        type="date" 
                        value={newAgreement.expiry_date}
                        onChange={(e) => setNewAgreement({...newAgreement, expiry_date: e.target.value})}
                        className={isDark ? "bg-slate-950 border-slate-800" : ""}
                      />
                    </div>
                  </div>

                  <div>
                    <Label className={cn("block mb-2", isDark ? "text-slate-300" : "")}>Data Categories</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="flex items-center gap-2">
                        <Checkbox 
                          checked={newAgreement.share_incidents}
                          onCheckedChange={(c) => setNewAgreement({...newAgreement, share_incidents: c})}
                        />
                        <span className={cn("text-sm", isDark ? "text-slate-300" : "")}>Incidents</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox 
                          checked={newAgreement.share_risks}
                          onCheckedChange={(c) => setNewAgreement({...newAgreement, share_risks: c})}
                        />
                        <span className={cn("text-sm", isDark ? "text-slate-300" : "")}>Risks</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox 
                          checked={newAgreement.share_component_data}
                          onCheckedChange={(c) => setNewAgreement({...newAgreement, share_component_data: c})}
                        />
                        <span className={cn("text-sm", isDark ? "text-slate-300" : "")}>Component Data</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox 
                          checked={newAgreement.share_life_limit_alerts}
                          onCheckedChange={(c) => setNewAgreement({...newAgreement, share_life_limit_alerts: c})}
                        />
                        <span className={cn("text-sm", isDark ? "text-slate-300" : "")}>Life-Limit Alerts</span>
                      </div>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreateAgreement} className="bg-sky-600 hover:bg-sky-700">
                    Create Agreement
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Agreements List */}
          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-0">
              {agreements.length === 0 ? (
                <div className="p-8 text-center">
                  <Handshake className="w-12 h-12 mx-auto mb-3 opacity-50 text-slate-400" />
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                    No data sharing agreements yet
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className={isDark ? "border-slate-800" : ""}>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Agreement</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Parties</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Level</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Status</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agreements.map(agr => (
                      <TableRow key={agr.id} className={isDark ? "border-slate-800" : ""}>
                        <TableCell>
                          <div className={cn("font-mono font-medium", isDark ? "text-white" : "")}>
                            {agr.agreement_number}
                          </div>
                          <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                            Effective: {agr.effective_date}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className={cn("text-sm", isDark ? "text-white" : "")}>
                            {agr.sharer_name}
                          </div>
                          <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                            → {agr.recipient_name}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-sky-500/20 text-sky-400 capitalize">
                            {agr.sharing_level}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={cn("capitalize border", STATUS_COLORS[agr.status])}>
                            {agr.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {agr.status === 'pending' && (
                              <Button 
                                size="sm" 
                                className="bg-green-600 hover:bg-green-700 gap-1"
                                onClick={() => handleApproveAgreement(agr.id)}
                              >
                                <Check className="w-3 h-3" />
                                Approve
                              </Button>
                            )}
                            {agr.status === 'active' && (
                              <Button 
                                size="sm" 
                                variant="destructive"
                                className="gap-1"
                                onClick={() => handleRevokeAgreement(agr.id)}
                              >
                                <X className="w-3 h-3" />
                                Revoke
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Airline Groups Tab */}
        <TabsContent value="groups" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className={cn("text-lg font-semibold", isDark ? "text-white" : "")}>
              Airline Groups
            </h2>
            <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
              <DialogTrigger asChild>
                <Button className="bg-purple-600 hover:bg-purple-700 gap-2">
                  <Plus className="w-4 h-4" />
                  New Group
                </Button>
              </DialogTrigger>
              <DialogContent className={cn("max-w-lg", isDark ? "bg-slate-900 border-slate-800" : "")}>
                <DialogHeader>
                  <DialogTitle className={isDark ? "text-white" : ""}>Create Airline Group</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className={isDark ? "text-slate-300" : ""}>Group Name *</Label>
                    <Input 
                      value={newGroup.group_name}
                      onChange={(e) => setNewGroup({...newGroup, group_name: e.target.value})}
                      placeholder="e.g., Saudi Aviation Holding"
                      className={isDark ? "bg-slate-950 border-slate-800" : ""}
                    />
                  </div>

                  <div>
                    <Label className={isDark ? "text-slate-300" : ""}>Group Type</Label>
                    <Select 
                      value={newGroup.group_type} 
                      onValueChange={(v) => setNewGroup({...newGroup, group_type: v})}
                    >
                      <SelectTrigger className={isDark ? "bg-slate-950 border-slate-800" : ""}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                        <SelectItem value="holding_company">Holding Company</SelectItem>
                        <SelectItem value="alliance">Airline Alliance</SelectItem>
                        <SelectItem value="franchise_network">Franchise Network</SelectItem>
                        <SelectItem value="codeshare_agreement">Codeshare Agreement</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className={isDark ? "text-slate-300" : ""}>Parent Organization</Label>
                    <Select 
                      value={newGroup.parent_organization_id || 'none'} 
                      onValueChange={(v) => setNewGroup({...newGroup, parent_organization_id: v === 'none' ? '' : v})}
                    >
                      <SelectTrigger className={isDark ? "bg-slate-950 border-slate-800" : ""}>
                        <SelectValue placeholder="Select parent organization..." />
                      </SelectTrigger>
                      <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                        <SelectItem value="none">None</SelectItem>
                        {organizations.map(org => (
                          <SelectItem key={org.id} value={org.id}>{org.legal_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <Checkbox 
                      checked={newGroup.consolidate_safety_metrics}
                      onCheckedChange={(c) => setNewGroup({...newGroup, consolidate_safety_metrics: c})}
                    />
                    <span className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                      Enable consolidated safety metrics
                    </span>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowGroupDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreateGroup} className="bg-purple-600 hover:bg-purple-700">
                    Create Group
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Groups List */}
          {groups.length === 0 ? (
            <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
              <CardContent className="p-8 text-center">
                <Network className="w-12 h-12 mx-auto mb-3 opacity-50 text-slate-400" />
                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                  No airline groups configured
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {groups.map(group => (
                <Card key={group.id} className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className={cn("text-lg", isDark ? "text-white" : "")}>
                          {group.group_name}
                        </CardTitle>
                        <CardDescription>{group.group_number}</CardDescription>
                      </div>
                      <Badge className="bg-purple-500/20 text-purple-400 capitalize">
                        {group.group_type?.replace('_', ' ')}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-slate-400" />
                        <span className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                          {group.members?.length || 0} members
                        </span>
                      </div>
                      {group.consolidate_safety_metrics && (
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-green-400" />
                          <span className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                            Consolidated metrics enabled
                          </span>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Transfers Tab */}
        <TabsContent value="transfers" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className={cn("text-lg font-semibold", isDark ? "text-white" : "")}>
              Safety History Transfers
            </h2>
          </div>

          <Card className={cn(isDark ? "bg-slate-900/50 border-slate-800" : "bg-white")}>
            <CardContent className="p-0">
              {transfers.length === 0 ? (
                <div className="p-8 text-center">
                  <History className="w-12 h-12 mx-auto mb-3 opacity-50 text-slate-400" />
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-gray-500")}>
                    No safety history transfers recorded
                  </p>
                  <p className={cn("text-xs mt-1", isDark ? "text-slate-500" : "text-gray-400")}>
                    Transfers are created when aircraft change ownership or operator
                  </p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className={isDark ? "border-slate-800" : ""}>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Transfer</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Aircraft</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>From → To</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Items Transferred</TableHead>
                      <TableHead className={isDark ? "text-slate-300" : ""}>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transfers.map(trf => (
                      <TableRow key={trf.id} className={isDark ? "border-slate-800" : ""}>
                        <TableCell>
                          <div className={cn("font-mono font-medium", isDark ? "text-white" : "")}>
                            {trf.transfer_number}
                          </div>
                          <div className={cn("text-xs capitalize", isDark ? "text-slate-400" : "text-gray-500")}>
                            {trf.transfer_type?.replace('_', ' ')}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Plane className="w-4 h-4 text-sky-500" />
                            <span className={cn("font-mono", isDark ? "text-white" : "")}>
                              {trf.aircraft_registration}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                            {trf.from_organization_name || 'Unknown'}
                          </div>
                          <div className={cn("text-xs", isDark ? "text-slate-400" : "text-gray-500")}>
                            → {trf.to_organization_name || 'Unknown'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Badge className="bg-orange-500/20 text-orange-400 text-xs">
                              {trf.incidents_transferred} incidents
                            </Badge>
                            <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                              {trf.risks_transferred} risks
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className={cn("text-sm", isDark ? "text-slate-300" : "")}>
                          {trf.created_at ? format(new Date(trf.created_at), 'MMM d, yyyy') : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
